import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface PopoverPosition {
  top: number;
  bottom: number;
  left: number;
  width: number;
  maxHeight: number;
  openUpward: boolean;
  visible: boolean;
}

interface UsePopoverPositionOptions {
  open: boolean;
  onClose: () => void;
  minWidth?: number;
  gap?: number;
  closeOnScroll?: boolean;
  maxHeight?: number;
  minHeight?: number;
  viewportMargin?: number;
  repositionKey?: unknown;
}

export function usePopoverPosition<TTrigger extends HTMLElement = HTMLDivElement, TPopover extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  minWidth = 0,
  gap = 6,
  closeOnScroll = true,
  maxHeight = 256,
  minHeight = 96,
  viewportMargin = 8,
  repositionKey,
}: UsePopoverPositionOptions) {
  const triggerRef = useRef<TTrigger>(null);
  const popoverRef = useRef<TPopover>(null);
  const [position, setPosition] = useState<PopoverPosition>({
    top: -9999,
    bottom: 0,
    left: -9999,
    width: minWidth,
    maxHeight,
    openUpward: false,
    visible: false,
  });

  const reposition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentHeight = popoverRef.current.scrollHeight;
    const desiredHeight = Math.min(contentHeight, maxHeight);
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap - viewportMargin;
    const spaceAbove = triggerRect.top - gap - viewportMargin;
    const openUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
    const available = openUpward ? spaceAbove : spaceBelow;

    setPosition({
      top: triggerRect.bottom + gap,
      bottom: window.innerHeight - triggerRect.top + gap,
      left: triggerRect.left,
      width: Math.max(triggerRect.width, minWidth),
      maxHeight: Math.max(Math.min(maxHeight, available), minHeight),
      openUpward,
      visible: true,
    });
  }, [gap, minWidth, maxHeight, minHeight, viewportMargin]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition((prev) => ({
      ...prev,
      top: rect.bottom + gap,
      left: rect.left,
      width: Math.max(rect.width, minWidth),
      visible: false,
    }));
    reposition();
  }, [open, reposition, gap, minWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [repositionKey, open, reposition]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      onClose();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleScroll = (e: Event) => {
      if (popoverRef.current && e.target instanceof Node && popoverRef.current.contains(e.target)) return;
      if (closeOnScroll) onClose();
    };
    const handleResize = () => {
      if (closeOnScroll) onClose();
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, onClose, closeOnScroll]);

  return { triggerRef, popoverRef, position };
}
